import './mobxConfig';
import AppProvider from 'providers/AppProvider';
import React from 'react';
import ReactDOM from 'react-dom/client';
import BreakpointsProvider from 'providers/BreakpointsProvider';
import SettingsPanelProvider from 'providers/SettingsPanelProvider';
import { Provider } from 'react-redux';
import store from './redux/store';
import { RouterProvider } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { router } from 'Routes';
import ChatWidgetProvider from 'providers/ChatWidgetProvider';
import OAuth2Provider from './OAuthProvider'; // The new OAuth Provider
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './toastify.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import { DialogMount } from 'dialogs';
import ErrorBoundary from './ErrorBoundary';
import { getTelemetryConfig } from 'services/backendUrl';
import { initTelemetryLogger, installGlobalErrorHandlers, setAuthTokenProvider } from 'telemetry/logger';
import { getToken } from 'utils/HelperFunctions';
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

// Initialise browser telemetry as early as possible. Controlled entirely by
// runtime config (OTEL_ENABLED in config.json) — a no-op when disabled.
// Fire-and-forget: telemetry setup must never block or break app startup.
void getTelemetryConfig()
  .then(async (tc) => {
    const version = import.meta.env.VITE_VERSION as string | undefined;
    // Send the app JWT with telemetry so the backend can authenticate the
    // browser and derive its tenant (read live so token refreshes are picked up).
    setAuthTokenProvider(() => getToken() ?? undefined);
    initTelemetryLogger({
      enabled: tc.enabled,
      endpoint: tc.endpoint,
      environment: tc.environment,
      version,
      captureVerboseConsole: tc.captureVerboseConsole,
    });
    if (!tc.enabled) return;
    // Load the OTel Web SDK lazily (dynamic import → its own chunk) so the SDK
    // never ships in the main bundle when telemetry is disabled. Tracing
    // registers the Web tracer and the log<->trace context bridge, and makes
    // API calls propagate traceparent so the backend joins the same trace.
    const { initTracing } = await import('telemetry/tracing');
    initTracing({
      enabled: true,
      tracesUrl: tc.tracesUrl,
      environment: tc.environment,
      version,
      // Send the app JWT with span exports too (read live so token refreshes
      // are picked up), so traces authenticate the same way logs do and work
      // when the backend enforces NAP_TELEMETRY_REQUIRE_AUTH.
      getAuthToken: () => getToken() ?? undefined,
    });
    installGlobalErrorHandlers();
  })
  .catch(() => {
    /* telemetry must never break startup */
  });

root.render(
  <Provider store={store}>
    <React.StrictMode>
      <ErrorBoundary>
        <div id="toast-root">
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
        <DialogMount />
        <I18nextProvider i18n={i18n}>
          <AppProvider>
            <OAuth2Provider>
              <SettingsPanelProvider>
                <ChatWidgetProvider>
                  <BreakpointsProvider>
                    <RouterProvider router={router} future={{ v7_startTransition: true }} />
                  </BreakpointsProvider>
                </ChatWidgetProvider>
              </SettingsPanelProvider>
            </OAuth2Provider>
          </AppProvider>
        </I18nextProvider>
      </ErrorBoundary>
    </React.StrictMode>
  </Provider>
);
